<template>
  <div class="course-logistics-container">
    <Sidebar class="sidebar" />
    <div class="main-content">
      <div v-if="loading" class="loading text-center p-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading courses...</p>
      </div>
      <div v-else-if="error" class="alert alert-danger mx-3 mt-3">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ error }}
        <button type="button" class="btn-close float-end" @click="error = null"></button>
      </div>
      <div v-else>
        <div class="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
          <h2 class="h3 mb-0">📦 Course Logistics Management</h2>
          <button 
            @click="showCreateModal = true"
            class="btn btn-primary btn-sm"
          >
            <i class="bi bi-plus-circle"></i> Add New Course URL
          </button>
        </div>

        <!-- Course Grid -->
        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mx-2">
          <div 
            v-for="(course, index) in coursesData.data" 
            :key="course.id"
            class="col"
          >
            <div class="card h-100 shadow-sm">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">{{ course.course_name }}</h5>
                <div class="btn-group">
                  <button 
                    @click="openEditModal(course, index)"
                    class="btn btn-link text-primary p-0"
                    title="Edit"
                  >
                    <i class="bi bi-pencil-square"></i>
                  </button>
                  <button 
                    @click="confirmDelete(course.id, index)"
                    class="btn btn-link text-danger p-0"
                    title="Delete"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
              <div class="card-body">
                <dl class="row mb-0">
                  <dt class="col-sm-5">Course ID:</dt>
                  <dd class="col-sm-7">{{ course.course_id }}</dd>
                  <dt class="col-sm-5">Course Name:</dt>
                  <dd class="col-sm-7">{{ course.course_name }}</dd>
                  
                  <template v-if="expandedCard === index">
                    <template v-for="(value, key) in course" :key="key">
                      <dt v-if="key !== 'course_name' && key !== 'id'" class="col-sm-5 text-truncate">
                        {{ key.replace(/_/g, ' ') }}:
                      </dt>
                      <dd v-if="key !== 'course_name' && key !== 'id'" class="col-sm-7">
                        <template v-if="isURL(value)">
                          <a :href="value" target="_blank" class="text-decoration-none">
                            {{ value || 'N/A' }} <i class="bi bi-box-arrow-up-right"></i>
                          </a>
                        </template>
                        <template v-else>
                          {{ value || 'N/A' }}
                        </template>
                      </dd>
                    </template>
                    <div class="text-end mt-2">
                      <button 
                        @click="expandedCard = null"
                        class="btn btn-sm btn-outline-secondary"
                      >
                        <i class="bi bi-chevron-up"></i> Collapse
                      </button>
                    </div>
                  </template>
                </dl>
                <div v-if="expandedCard !== index" class="text-center">
                  <button 
                    @click="expandedCard = index"
                    class="btn btn-sm btn-link"
                  >
                    <i class="bi bi-chevron-down"></i> Show Details
                  </button>
                </div>
              </div>
              <div class="card-footer text-muted small">
                Updated {{ formatDate(course.updated_at) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <nav aria-label="Course pagination" class="my-4">
          <div class="text-center mb-2">Page {{ coursesData.current_page }} of {{ coursesData.last_page }}</div>
          <ul class="pagination justify-content-center">
            <li class="page-item" :class="{ disabled: !coursesData.prev_page_url }">
              <button 
                @click="loadPage(coursesData.current_page - 1)"
                class="page-link"
              >
                <i class="bi bi-chevron-left"></i>
              </button>
            </li>
            
            <li 
              v-for="page in coursesData.last_page" 
              :key="page"
              class="page-item" 
              :class="{ active: page === coursesData.current_page }"
            >
              <button 
                @click="loadPage(page)"
                class="page-link"
              >
                {{ page }}
              </button>
            </li>

            <li class="page-item" :class="{ disabled: !coursesData.next_page_url }">
              <button 
                @click="loadPage(coursesData.current_page + 1)"
                class="page-link"
              >
                <i class="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>

        <!-- Create/Edit Modal -->
        <div class="modal-backdrop fade" :class="{ show: showCreateModal || showEditModal }" v-if="showCreateModal || showEditModal" @click="closeModal"></div>
        <div class="modal fade" :class="{ show: showCreateModal || showEditModal }" style="display: block;" 
             :style="{ display: (showCreateModal || showEditModal) ? 'block' : 'none' }">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  <i class="bi bi-link-45deg"></i> 
                  {{ showCreateModal ? 'Create New' : 'Edit' }} Course URL
                </h5>
                <button type="button" class="btn-close" @click="closeModal"></button>
              </div>
              <form @submit.prevent="showCreateModal ? createCourse() : updateCourse()" class="needs-validation" novalidate>
                <div class="modal-body">
                  <div class="mb-3">
                    <label class="form-label">Select Lesson *</label>
                    <select 
                      v-model="selectedLessonId" 
                      @change="updateFormWithLesson"
                      class="form-select"
                      required
                    >
                      <option value="">Choose a Lesson</option>
                      <option 
                        v-for="lesson in lessons" 
                        :key="lesson.id" 
                        :value="lesson.id"
                      >
                        {{ lesson.title || `Lesson ${lesson.id}` }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a lesson</div>
                  </div>
                  
                  <div class="mb-3">
                    <label class="form-label">Course ID *</label>
                    <input
                      v-model="form.course_id"
                      type="number"
                      required
                      class="form-control"
                      :disabled="showEditModal || !!selectedLessonId"
                    />
                    <div class="invalid-feedback">Course ID is required</div>
                  </div>
                  
                  <div class="mb-3">
                    <label class="form-label">Course Name *</label>
                    <input
                      v-model="form.course_name"
                      type="text"
                      required
                      class="form-control"
                    />
                    <div class="invalid-feedback">Course name is required</div>
                  </div>
                  
                  <!-- Add other form fields here -->
                  <div v-for="field in formFields" :key="field.name" class="mb-3">
                    <label class="form-label">{{ field.label }}</label>
                    <input
                      v-model="form[field.name]"
                      :type="field.type"
                      class="form-control"
                      :required="field.required"
                    />
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
                  <button 
                    type="submit" 
                    class="btn btn-primary"
                    :disabled="!form.course_id || !selectedLessonId || formSubmitting"
                  >
                    <span v-if="formSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    {{ showCreateModal ? 'Create' : 'Save Changes' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div class="modal-backdrop fade" :class="{ show: showDeleteModal }" v-if="showDeleteModal" @click="showDeleteModal = false"></div>
        <div class="modal fade" :class="{ show: showDeleteModal }" style="display: block;" 
             :style="{ display: showDeleteModal ? 'block' : 'none' }">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header bg-danger text-white">
                <h5 class="modal-title">
                  <i class="bi bi-exclamation-triangle"></i> Confirm Delete
                </h5>
                <button type="button" class="btn-close btn-close-white" @click="showDeleteModal = false"></button>
              </div>
              <div class="modal-body">
                <p>Are you sure you want to delete this course URL? This action cannot be undone.</p>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                <button type="button" class="btn btn-danger" @click="deleteCourse" :disabled="formSubmitting">
                  <span v-if="formSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  <i class="bi bi-trash"></i> Confirm Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue'; 
export default {
  name: 'CourseLogistics',
  components: {
    Sidebar
  },
  data() {
    return {
      coursesData: {},
      loading: true,
      formSubmitting: false,
      error: null,
      form: {
        course_id: '',
        course_name: '',
        image_url: '',
        image_url_ar: '',
        downloadable_url: '',
        preview_temp_file: '',
        preview_video_id: '',
        preview_url: '',
        preview_thumbnail_url: '',
        science_behind_it: '',
        lesson_id: null
      },
      editIndex: -1,
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      expandedCard: null,
      deleteId: null,
      formFields: [
        { name: 'image_url', label: 'Image URL', type: 'url' },
        { name: 'image_url_ar', label: 'AR Image URL', type: 'url' },
        { name: 'downloadable_url', label: 'Download URL', type: 'url' },
        { name: 'preview_temp_file', label: 'Temp File', type: 'text' },
        { name: 'preview_video_id', label: 'Video ID', type: 'text' },
        { name: 'preview_url', label: 'Preview URL', type: 'url' },
        { name: 'preview_thumbnail_url', label: 'Thumbnail URL', type: 'url' },
        { name: 'science_behind_it', label: 'Science Behind It', type: 'text' }
      ],
      lessons: [],
      selectedLessonId: null,
    };
  },
  methods: {
    async loadPage(page) {
      try {
        this.loading = true;
        const response = await axios.get('https://api.kewaar.com/api/admin/course-urls1', {
          params: { page },
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });
        
        if (response.data.success) {
          this.coursesData = response.data.data;
          this.error = null;
        } else {
          this.error = 'Failed to load course data: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        this.error = 'Error fetching courses: ' + err.message;
        console.error('API Error:', err);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    async createCourse() {
      // Form validation
      if (!this.validateForm()) return;
      
      try {
        this.formSubmitting = true;
        const response = await axios.post(
          'https://api.kewaar.com/api/admin/course-urls1',
          this.form,
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }
        );
        
        if (response.data.success) {
          // Add the new course to the beginning of the list
          if (Array.isArray(this.coursesData.data)) {
            this.coursesData.data.unshift(response.data.data);
          }
          this.closeModal();
          this.error = null;
        } else {
          this.error = `Create failed: ${response.data.message || 'Unknown error'}`;
        }
      } catch (error) {
        this.error = `Create failed: ${error.response?.data?.message || error.message}`;
        console.error('API Error:', error);
      } finally {
        this.loading = false;
        this.formSubmitting = false;
        if (this.error) {
          setTimeout(() => this.error = null, 5000);
        }
      }
    },
    openEditModal(course, index) {
      this.form = { ...course };
      this.selectedLessonId = course.lesson_id;
      this.editIndex = index;
      this.showEditModal = true;
    },
    async updateCourse() {
      // Form validation
      if (!this.validateForm()) return;
      
      try {
        this.formSubmitting = true;
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/course-urls1/${this.form.id}`,
          this.form,
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }
        );
        
        if (response.data.success) {
          // Replace the updated course in the array
          if (Array.isArray(this.coursesData.data) && this.editIndex >= 0) {
            this.coursesData.data.splice(this.editIndex, 1, response.data.data);
          }
          this.closeModal();
          this.error = null;
        } else {
          this.error = `Update failed: ${response.data.message || 'Unknown error'}`;
        }
      } catch (error) {
        this.error = `Update failed: ${error.response?.data?.message || error.message}`;
        console.error('API Error:', error);
      } finally {
        this.loading = false;
        this.formSubmitting = false;
        if (this.error) {
          setTimeout(() => this.error = null, 5000);
        }
      }
    },
    confirmDelete(id, index) {
      this.deleteId = id;
      this.editIndex = index;
      this.showDeleteModal = true;
    },
    async deleteCourse() {
      try {
        this.formSubmitting = true;
        await axios.delete(
          `https://api.kewaar.com/api/admin/course-urls1/${this.deleteId}`,
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }
        );
        this.coursesData.data.splice(this.editIndex, 1);
        this.showDeleteModal = false;
      } catch (error) {
        this.error = `Delete failed: ${error.response?.data?.message || error.message}`;
        setTimeout(() => this.error = null, 5000);
        this.showDeleteModal = false;
      } finally {
        this.formSubmitting = false;
      }
    },
    closeModal() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.form = {
        course_id: '',
        course_name: '',
        image_url: '',
        image_url_ar: '',
        downloadable_url: '',
        preview_temp_file: '',
        preview_video_id: '',
        preview_url: '',
        preview_thumbnail_url: '',
        science_behind_it: '',
        lesson_id: null
      };
      this.selectedLessonId = null;
      this.editIndex = -1;
    },
    isURL(value) {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    async fetchLessons() {
      try {
        const response = await axios.get('https://api.kewaar.com/api/admin/all-lessons', {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
          }
        });
        console.log('Lessons response:', response.data);
        
        // Updated response structure handling
        if (response.data?.success) {
          if (Array.isArray(response.data.data)) {
            this.lessons = response.data.data;
          } else if (response.data.data?.lessons && Array.isArray(response.data.data.lessons)) {
            this.lessons = response.data.data.lessons;
          } else {
            console.error('Unexpected lessons data structure:', response.data);
            this.lessons = [];
          }
        } else {
          this.lessons = [];
        }
      } catch (error) {
        console.error('Error fetching lessons:', error);
        this.lessons = [];
      }
    },
    updateFormWithLesson() {
      const selectedLesson = this.lessons.find(lesson => lesson.id === this.selectedLessonId);
      if (selectedLesson) {
        // Store current form values that should be preserved
        const currentFormId = this.form.id;
        const currentCourseName = this.form.course_name;
        
        // Create a new form object with default values
        const newForm = {
          course_id: '',
          course_name: '',
          image_url: '',
          image_url_ar: '',
          downloadable_url: '',
          preview_temp_file: '',
          preview_video_id: '',
          preview_url: '',
          preview_thumbnail_url: '',
          science_behind_it: '',
          lesson_id: null
        };
        
        // If editing, preserve the ID
        if (this.showEditModal && currentFormId) {
          newForm.id = currentFormId;
        }
        
        // Map lesson data to form fields
        newForm.image_url = selectedLesson.course?.image_url || '';
        newForm.image_url_ar = selectedLesson.course?.image_url_ar || '';
        newForm.downloadable_url = selectedLesson.downloadable_url || '';
        newForm.preview_temp_file = selectedLesson.lesson_temp_file || '';
        newForm.preview_video_id = selectedLesson.lesson_video_id || '';
        newForm.preview_url = selectedLesson.lesson_url || '';
        newForm.preview_thumbnail_url = selectedLesson.lesson_thumbnail_url || '';
        newForm.science_behind_it = selectedLesson.course?.science_behind_it || '';
        newForm.lesson_id = selectedLesson.id;
        
        // Set course name - prefer existing value if editing
        if (this.showEditModal && currentCourseName) {
          newForm.course_name = currentCourseName;
        } else {
          newForm.course_name = selectedLesson.course?.title || '';
        }
        
        // If creating new, set course_id from lesson's course
        if (this.showCreateModal && selectedLesson.course?.id) {
          newForm.course_id = selectedLesson.course.id;
        } else if (this.showEditModal) {
          // Keep existing course_id when editing
          newForm.course_id = this.form.course_id;
        }
        
        // Update the form
        this.form = newForm;
      }
    },
    validateForm() {
      // Check if form is valid using Bootstrap's validation
      const form = document.querySelector('.needs-validation');
      if (!form.checkValidity()) {
        form.classList.add('was-validated');
        return false;
      }
      return true;
    },
  },
  async created() {
    await this.loadPage(1);
    await this.fetchLessons();
  }
};
</script>

<style scoped>
.course-logistics-container {
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.sidebar {
  flex: 0 0 250px;
  height: 100vh;
  position: sticky;
  top: 0;
}

.main-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  height: 100vh;
}

.modal {
  z-index: 1050;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal-dialog {
  position: relative;
}

.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

.page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
}
</style> 